import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaChevronLeft, FaChevronRight, FaExpand } from 'react-icons/fa';
import { useLanguage } from '../contexts/LanguageContext';
import './TrainingExperience.css';

/** Apsolutne putanje; PUBLIC_URL pokriva deploy u podfolder (npr. /app) */
const TRAINING_IMAGE_PATHS = [
  '/images/training-experience/IMG_2756.jpg',
  '/images/training-experience/IMG_2776.jpg',
  '/images/training-experience/IMG_2786.JPG',
  '/images/training-experience/IMG_2853.jpg',
  '/images/training-experience/IMG_2923.jpg',
  '/images/training-experience/IMG_2924.jpg',
  '/images/training-experience/IMG_2925.jpg',
  '/images/training-experience/IMG_2948.jpg',
  '/images/training-experience/IMG_3026.jpg',
  '/images/training-experience/IMG_3037.jpg',
  '/images/training-experience/IMG_3053.jpg',
  '/images/training-experience/IMG_3101.jpg',
  '/images/training-experience/IMG_3110.jpg',
  '/images/training-experience/IMG_3128.jpg',
  '/images/training-experience/IMG_3140.JPG',
  '/images/training-experience/IMG_3143.jpg',
  '/images/training-experience/IMG_3147.jpg',
];

/** Koliko prvih thumbova učitava odmah (ostatak lazy — brži prvi prikaz na mobilnom). */
const EAGER_THUMB_COUNT = 4;

/** Lake verzije za mrežu (generiše `npm run images:training-thumbs`). */
function getThumbPath(fullPath: string): string {
  const file = fullPath.split('/').pop() || '';
  const base = file.replace(/\.[^.]+$/i, '');
  return `/images/training-experience/thumbs/${base}.jpg`;
}

const TrainingExperience: React.FC = () => {
  const { t } = useLanguage();
  const [isMobile, setIsMobile] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  /** Kada je thumb slika spremna — skida skeleton i pali fade-in */
  const [thumbLoaded, setThumbLoaded] = useState<Record<number, boolean>>({});
  /** Puna rezolucija u modalu spremna (ispod se do tada vidi brzi preview thumb) */
  const [modalFullLoaded, setModalFullLoaded] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const closeModal = useCallback(() => setSelectedIndex(null), []);

  const markThumbLoaded = useCallback((index: number) => {
    setThumbLoaded((prev) => (prev[index] ? prev : { ...prev, [index]: true }));
  }, []);

  const resolvePublicUrl = useCallback((path: string) => `${process.env.PUBLIC_URL || ''}${path}`, []);

  const goToPrevious = useCallback(() => {
    setSelectedIndex((i) => {
      if (i === null) return null;
      return i === 0 ? TRAINING_IMAGE_PATHS.length - 1 : i - 1;
    });
  }, []);

  const goToNext = useCallback(() => {
    setSelectedIndex((i) => {
      if (i === null) return null;
      return i === TRAINING_IMAGE_PATHS.length - 1 ? 0 : i + 1;
    });
  }, []);

  useEffect(() => {
    if (selectedIndex === null) return;
    setModalFullLoaded(false);
  }, [selectedIndex]);

  useEffect(() => {
    if (selectedIndex === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeModal();
      if (e.key === 'ArrowLeft') goToPrevious();
      if (e.key === 'ArrowRight') goToNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedIndex, closeModal, goToPrevious, goToNext]);

  return (
    <section id="training-experience" className="training-exp section-lg">
      <div className="container">
        <motion.div
          className="training-exp__header"
          initial={isMobile ? { opacity: 1, y: 0 } : { opacity: 0, y: 28 }}
          whileInView={isMobile ? { opacity: 1, y: 0 } : { opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="training-exp__header-mark" aria-hidden />
          <h2 className="training-exp__title">{t('trainingExperience.title')}</h2>
          <p className="training-exp__subtitle">{t('trainingExperience.subtitle')}</p>
        </motion.div>

        <motion.div
          className="training-exp__narrative"
          initial={isMobile ? { opacity: 1, y: 0 } : { opacity: 0, y: 22 }}
          whileInView={isMobile ? { opacity: 1, y: 0 } : { opacity: 1, y: 0 }}
          transition={{ duration: 0.55, delay: 0.05 }}
          viewport={{ once: true }}
        >
          <span className="training-exp__narrative-glow" aria-hidden />
          <div className="training-exp__narrative-inner">
          <p className="training-exp__paragraph">{t('trainingExperience.paragraph1')}</p>
          <p className="training-exp__paragraph">{t('trainingExperience.paragraph2')}</p>
          <p className="training-exp__paragraph">{t('trainingExperience.paragraph3')}</p>
          <p className="training-exp__paragraph">{t('trainingExperience.paragraph4')}</p>
          </div>
        </motion.div>

        <motion.div
          className="training-exp__gallery"
          initial={isMobile ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }}
          whileInView={isMobile ? { opacity: 1, y: 0 } : { opacity: 1, y: 0 }}
          transition={{ duration: 0.55, delay: 0.1 }}
          viewport={{ once: true }}
        >
          <div className="training-exp__gallery-head" aria-hidden />
          <div className="training-exp__grid">
            {TRAINING_IMAGE_PATHS.map((src, index) => {
              const isFeature = index % 9 === 0;
              const resolvedThumb = resolvePublicUrl(getThumbPath(src));
              const isLoaded = thumbLoaded[index];
              const eager = index < EAGER_THUMB_COUNT;
              return (
                <div
                  key={src}
                  role="button"
                  tabIndex={0}
                  className={
                    isFeature
                      ? 'training-exp__item training-exp__item--feature'
                      : 'training-exp__item'
                  }
                  onClick={() => setSelectedIndex(index)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setSelectedIndex(index);
                    }
                  }}
                  aria-label={`${t('trainingExperience.imageAlt')} ${index + 1}`}
                >
                  <span
                    className={
                      isLoaded
                        ? 'training-exp__item-mat training-exp__item-mat--loaded'
                        : 'training-exp__item-mat'
                    }
                  >
                    <img
                      src={resolvedThumb}
                      alt="Trening škole fudbala FK Junior Novi Sad u Valencia Academy"
                      className="training-exp__img"
                      loading={eager ? 'eager' : 'lazy'}
                      decoding="async"
                      draggable={false}
                      sizes="(max-width: 480px) 46vw, (max-width: 1100px) 31vw, 23vw"
                      fetchPriority={index === 0 ? 'high' : index < EAGER_THUMB_COUNT ? 'auto' : 'low'}
                      onLoad={() => markThumbLoaded(index)}
                      onError={() => markThumbLoaded(index)}
                    />
                  </span>
                  <span className="training-exp__item-overlay" aria-hidden>
                    <FaExpand className="training-exp__item-expand" />
                  </span>
                </div>
              );
            })}
          </div>
        </motion.div>
      </div>

      <AnimatePresence>
        {selectedIndex !== null && (
          <motion.div
            className="training-exp__modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={closeModal}
            role="presentation"
          >
            <motion.div
              className="training-exp__modal-inner"
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.92, opacity: 0 }}
              transition={{ duration: 0.25 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="training-exp__modal-close"
                onClick={closeModal}
                aria-label="Close"
              >
                <FaTimes />
              </button>
              <button
                type="button"
                className="training-exp__modal-nav training-exp__modal-prev"
                onClick={(e) => {
                  e.stopPropagation();
                  goToPrevious();
                }}
                aria-label="Previous"
              >
                <FaChevronLeft />
              </button>
              <button
                type="button"
                className="training-exp__modal-nav training-exp__modal-next"
                onClick={(e) => {
                  e.stopPropagation();
                  goToNext();
                }}
                aria-label="Next"
              >
                <FaChevronRight />
              </button>
              <div
                className={
                  modalFullLoaded
                    ? 'training-exp__modal-image-wrap training-exp__modal-image-wrap--full-ready'
                    : 'training-exp__modal-image-wrap'
                }
              >
                <img
                  src={resolvePublicUrl(getThumbPath(TRAINING_IMAGE_PATHS[selectedIndex]))}
                  alt="Trening škole fudbala FK Junior Novi Sad u Valencia Academy"
                  className="training-exp__modal-preview"
                  decoding="async"
                  draggable={false}
                />
                {!modalFullLoaded && (
                  <div className="training-exp__modal-spinner" role="status" aria-label="Loading" />
                )}
                <motion.img
                  key={selectedIndex}
                  src={resolvePublicUrl(TRAINING_IMAGE_PATHS[selectedIndex])}
                  alt="Trening škole fudbala FK Junior Novi Sad u Valencia Academy"
                  className="training-exp__modal-img"
                  decoding="async"
                  fetchPriority="high"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: modalFullLoaded ? 1 : 0 }}
                  transition={{ duration: 0.35 }}
                  onLoad={() => setModalFullLoaded(true)}
                  onError={() => setModalFullLoaded(true)}
                />
              </div>
              <p className="training-exp__modal-counter">
                {selectedIndex + 1} / {TRAINING_IMAGE_PATHS.length}
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default TrainingExperience;
